/** Shared GLSL function strings used across multiple GPU adjustment
 *  shaders. Each constant is meant to be string-concatenated into a
 *  shader source between the precision declaration and `void main()`.
 *
 *  Goal is exact byte-parity with the CPU adjustment math. Each
 *  helper mirrors the inlined per-channel code in the matching JS
 *  implementation so a contributor reading both side-by-side sees
 *  the same operations in the same order.
 *
 *  ── Why GLSL strings (vs. a single shared "prelude" file) ───────
 *  WebGL doesn't have a #include directive. We can't reuse helpers
 *  across shaders any way other than concatenating source strings
 *  at shader-compile time. Keeping the helpers in one place
 *  (instead of duplicating per shader) ensures Hue/Sat and Vibrance
 *  apply exactly the same HSL round-trip — divergence here would
 *  silently change byte parity with the CPU path.
 *
 *  ── DO NOT use backtick characters (`) inside shader source ─────
 *  Shader sources live inside JS template literals, and a backtick
 *  inside the shader string (even in a GLSL comment) terminates the
 *  outer template literal early. TypeScript then tries to parse the
 *  rest of the shader as JS and reports confusing "name not found"
 *  errors at compile time. Stick to single / double quotes when
 *  referencing things in shader comments. */

import {
  BT601_CB_B,
  BT601_CB_G,
  BT601_CB_R,
  BT601_CB_TO_B,
  BT601_CB_TO_G,
  BT601_CR_B,
  BT601_CR_G,
  BT601_CR_R,
  BT601_CR_TO_G,
  BT601_CR_TO_R,
  BT601_LUMA_B,
  BT601_LUMA_G,
  BT601_LUMA_R,
  LUMA_B,
  LUMA_G,
  LUMA_R,
} from '../color-constants';

/** Rec. 709 luma weights as a GLSL prelude. Values come from the
 *  canonical CPU constants — interpolating them via template literal
 *  guarantees the GPU shader and CPU adjustment code never drift
 *  apart. `toFixed(6)` is enough decimal precision to round-trip
 *  through GLSL's `highp float` exactly. */
export const LUMA_GLSL = `
const float LUMA_R = ${LUMA_R.toFixed(6)};
const float LUMA_G = ${LUMA_G.toFixed(6)};
const float LUMA_B = ${LUMA_B.toFixed(6)};
`;

/** BT.601 luma weights as a GLSL prelude. Same drift-prevention
 *  rationale as LUMA_GLSL — the constants come from the canonical
 *  CPU export, interpolated via template literal at compile time
 *  so the BT.601-using shaders (Threshold, Find Edges) can never
 *  drift from the JS path's weights. */
export const BT601_LUMA_GLSL = `
const float BT601_LUMA_R = ${BT601_LUMA_R.toFixed(6)};
const float BT601_LUMA_G = ${BT601_LUMA_G.toFixed(6)};
const float BT601_LUMA_B = ${BT601_LUMA_B.toFixed(6)};
`;

/** RGB → HSL. Input rgb is normalized [0, 1]; output is
 *  (hue ∈ [0, 360], saturation ∈ [0, 1], lightness ∈ [0, 1]).
 *  Matches the per-pixel inline math in
 *  `lib/adjustments/hue-saturation.ts` / `vibrance.ts`. */
export const RGB_TO_HSL_GLSL = `
vec3 rgbToHsl(vec3 rgb) {
  float mx = max(max(rgb.r, rgb.g), rgb.b);
  float mn = min(min(rgb.r, rgb.g), rgb.b);
  float l = (mx + mn) * 0.5;
  if (mx == mn) return vec3(0.0, 0.0, l);
  float d = mx - mn;
  float s = l > 0.5 ? d / (2.0 - mx - mn) : d / (mx + mn);
  float h;
  if (mx == rgb.r) h = ((rgb.g - rgb.b) / d + (rgb.g < rgb.b ? 6.0 : 0.0)) * 60.0;
  else if (mx == rgb.g) h = ((rgb.b - rgb.r) / d + 2.0) * 60.0;
  else h = ((rgb.r - rgb.g) / d + 4.0) * 60.0;
  return vec3(h, s, l);
}
`;

/** BT.601 YCbCr conversion in normalized [0, 1] space (chroma
 *  offset = 128/255 ≈ 0.5019607843). All weights interpolate from
 *  the canonical CPU constants so the GPU shader and the CPU
 *  Smart Sharpen filter can't drift apart.
 *
 *  The CPU code does the math in 0..255 space; here we do it in
 *  0..1. The transform matrix is the same (linear, scale-
 *  invariant); the 128 offset becomes 128.0/255.0; thresholds /
 *  halo limits passed as uniforms scale to 0..1 likewise. Round-
 *  tripping rgb -> ycbcr -> rgb is a no-op modulo FP precision.
 *
 *  Three function-level helpers:
 *    - `rgbToY601` — luma-only projection; call this when you need
 *      Y but will discard Cb/Cr (e.g. Smart Sharpen's blurred copy).
 *    - `rgbToYCbCr601` — full forward transform.
 *    - `yCbCrToRgb601` — inverse transform. */
export const BT601_YCBCR_GLSL = `
const float BT601_CHROMA_OFFSET = 128.0 / 255.0;

float rgbToY601(vec3 rgb) {
  return ${BT601_LUMA_R.toFixed(6)} * rgb.r + ${BT601_LUMA_G.toFixed(6)} * rgb.g + ${BT601_LUMA_B.toFixed(6)} * rgb.b;
}

vec3 rgbToYCbCr601(vec3 rgb) {
  float y  = rgbToY601(rgb);
  float cb = ${BT601_CB_R.toFixed(6)} * rgb.r + ${BT601_CB_G.toFixed(6)} * rgb.g + ${BT601_CB_B.toFixed(6)} * rgb.b + BT601_CHROMA_OFFSET;
  float cr = ${BT601_CR_R.toFixed(6)} * rgb.r + ${BT601_CR_G.toFixed(6)} * rgb.g + ${BT601_CR_B.toFixed(6)} * rgb.b + BT601_CHROMA_OFFSET;
  return vec3(y, cb, cr);
}

vec3 yCbCrToRgb601(vec3 yCbCr) {
  float cb = yCbCr.y - BT601_CHROMA_OFFSET;
  float cr = yCbCr.z - BT601_CHROMA_OFFSET;
  float r = yCbCr.x + ${BT601_CR_TO_R.toFixed(6)} * cr;
  float g = yCbCr.x + ${BT601_CB_TO_G.toFixed(6)} * cb + ${BT601_CR_TO_G.toFixed(6)} * cr;
  float b = yCbCr.x + ${BT601_CB_TO_B.toFixed(6)} * cb;
  return vec3(r, g, b);
}
`;

/** HSL → RGB. Input hsl is (hue ∈ [0, 360], sat ∈ [0, 1],
 *  light ∈ [0, 1]); output is normalized RGB.
 *
 *  `hueToChannel` is the standard piecewise interpolator (matches
 *  Wikipedia's HSL→RGB reference). The CPU code unrolls this
 *  per-channel; we keep it factored to a single function so the
 *  shader source stays readable.
 *
 *  `mod(t + 1.0, 1.0)` handles the JS wraparound (`if (tR < 0) tR
 *  += 1; else if (tR > 1) tR -= 1`) — applied componentwise so all
 *  three channel offsets wrap in parallel. */
export const HSL_TO_RGB_GLSL = `
float hueToChannel(float p, float q, float t) {
  if (t < 1.0 / 6.0) return p + (q - p) * 6.0 * t;
  if (t < 1.0 / 2.0) return q;
  if (t < 2.0 / 3.0) return p + (q - p) * (2.0 / 3.0 - t) * 6.0;
  return p;
}

vec3 hslToRgb(vec3 hsl) {
  float h = hsl.x;
  float s = hsl.y;
  float l = hsl.z;
  if (s < 1e-6) return vec3(l);
  float q = l < 0.5 ? l * (1.0 + s) : l + s - l * s;
  float p = 2.0 * l - q;
  float hk = h / 360.0;
  vec3 t = mod(vec3(hk + 1.0 / 3.0, hk, hk - 1.0 / 3.0) + 1.0, 1.0);
  return vec3(
    hueToChannel(p, q, t.x),
    hueToChannel(p, q, t.y),
    hueToChannel(p, q, t.z)
  );
}
`;
